import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Field, FieldError, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  type DotLookupResult,
  fillApplicationError,
  fillApplicationErrorMessage,
  FillErrorCode,
  type FillOrgRef,
  lookupDot,
  saveDot,
  useFillApi,
} from '@/models/fillApplication'
import { DotLookupPreview } from './DotLookupPreview'
import { DuplicateCustomerNotice } from './DuplicateCustomerNotice'
import { TestDotButtons } from './TestDotButtons'

const NOT_FOUND = 'FMCSA has no carrier with that DOT.'
const NOT_RESPONDING = "FMCSA lookup isn't responding. Try again."

function dotErrorMessage(error: unknown): string {
  const refusal = fillApplicationError(error)
  if (refusal?.code === FillErrorCode.DOT_NOT_FOUND) return NOT_FOUND
  if (refusal?.code === FillErrorCode.DOT_LOOKUP_FAILED) return NOT_RESPONDING
  const status = (error as { response?: { status?: number } } | null | undefined)?.response?.status
  if (!refusal && status === 404) return NOT_FOUND
  if (!refusal && (!status || status >= 500)) return NOT_RESPONDING
  // Other refusals, and rate limits (a plain 403), carry their own message.
  return fillApplicationErrorMessage(error)
}

interface FmcsaStepProps {
  orgId: string
  initialDot?: string
  /** Another active customer holding the DOT already on file. */
  duplicateOf?: FillOrgRef | null
  /** The button that picks the looked-up carrier. */
  confirmLabel?: string
  onSaved: () => void
}

export function FmcsaStep({
  orgId,
  initialDot = '',
  duplicateOf = null,
  confirmLabel = 'Use this carrier',
  onSaved,
}: FmcsaStepProps) {
  const api = useFillApi()
  const [dot, setDot] = useState(initialDot)
  const [preview, setPreview] = useState<DotLookupResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [looking, setLooking] = useState(false)
  const [saving, setSaving] = useState(false)

  function changeDot(next: string) {
    setDot(next.replace(/\D/g, '').slice(0, 8))
    setPreview(null)
    setError(null)
  }

  async function lookUp() {
    if (!dot) return
    setLooking(true)
    setError(null)
    try {
      setPreview(await lookupDot(orgId, dot, api))
    } catch (lookupError) {
      setPreview(null)
      setError(dotErrorMessage(lookupError))
    } finally {
      setLooking(false)
    }
  }

  async function chooseCarrier() {
    if (!preview) return
    setSaving(true)
    setError(null)
    try {
      await saveDot(orgId, preview.dotNumber, api)
      onSaved()
    } catch (saveError) {
      setError(dotErrorMessage(saveError))
    } finally {
      setSaving(false)
    }
  }

  const duplicate = preview ? preview.duplicateOf : dot === initialDot ? duplicateOf : null

  return (
    <div className="flex flex-col gap-4">
      <Field>
        <FieldLabel htmlFor="fill-dot-number">DOT number</FieldLabel>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            id="fill-dot-number"
            inputMode="numeric"
            autoComplete="off"
            value={dot}
            onChange={(event) => changeDot(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                void lookUp()
              }
            }}
            aria-invalid={!!error || undefined}
            className="max-w-48"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => void lookUp()}
            disabled={!dot}
            loading={looking ? 'Looking up…' : undefined}
          >
            Look up
          </Button>
          <TestDotButtons onPick={changeDot} disabled={looking || saving} />
        </div>
        <FieldError>{error}</FieldError>
      </Field>
      {duplicate && <DuplicateCustomerNotice duplicate={duplicate} />}
      {preview && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-4">
          <DotLookupPreview result={preview} />
          <Button onClick={() => void chooseCarrier()} loading={saving ? 'Saving…' : undefined}>
            {confirmLabel}
          </Button>
        </div>
      )}
    </div>
  )
}
